import { AxiosResponse } from 'axios';
import request from '@core/services/request';

import DealChecklistItem, { IChecklistVerification } from '@core/classes/Checklist/DealChecklistItem';
import { FIMenuSection, PersonType } from '@core/classes/SharedEnums';
import { IChecklistAddCustomRequest } from '@core/classes/Checklist/ChecklistRequests';
import { IChecklistMetadataResponse } from '@core/classes/Checklist/ChecklistMetadata';
import IElasticResponseClass from '@core/classes/Responses/ElasticResponseClass';
import ISimpleResponse from '@core/classes/Responses/SimpleResponse';
import { RichTableServerSide } from '@core/classes/RichTableServerSide';
import SettingsChecklistItem from '@core/classes/Checklist/SettingsChecklistItem';

export const checklistAPI = {
	// #region Config Routes
	config_bulkUpdate: (alteredOrAddedItems: SettingsChecklistItem[], deletedItemIds: string[]): Promise<AxiosResponse<boolean>> =>
		request.post(`/Checklist/Config/BulkUpdate`, {
			itemsToUpdate: alteredOrAddedItems,
			itemIdsToDelete: deletedItemIds,
		}),

	config_upsertItem: (item: Partial<SettingsChecklistItem>): Promise<AxiosResponse<IElasticResponseClass>> => request.post(`/Checklist/Config/UpsertItem`, item),

	config_deleteItem: (id: string): Promise<AxiosResponse<IElasticResponseClass>> => request.post(`/Checklist/Config/DeleteItem?id=${id}`),

	config_getAllItemsPaged: (pagedRequest: RichTableServerSide): Promise<AxiosResponse<RichTableServerSide>> =>
		request.post(`/Checklist/Config/GetItemsPaged`, pagedRequest),

	config_getAllItems: (): Promise<AxiosResponse<SettingsChecklistItem[]>> => request.get(`/Checklist/Config/GetAllItems`),

	config_getItem: (id: string): Promise<AxiosResponse<SettingsChecklistItem>> => request.get(`/Checklist/Config/GetItem?id=${id}`),

	config_getMetadataFields: (): Promise<AxiosResponse<IChecklistMetadataResponse[]>> => request.get(`/Checklist/Config/GetMetadataFieldsForAllTypes`),
	// #endregion

	// #region FIMenu Routes
	fimenu_getAllRequired: (dealId: string, section: FIMenuSection = FIMenuSection.Unknown): Promise<AxiosResponse<DealChecklistItem[]>> =>
		request.get(`/Checklist/FIMenu/GetAllRequired?dealId=${dealId}&section=${section}`),

	fimenu_getAllRequiredByDealNumber: (dealNumber: string, section: FIMenuSection = FIMenuSection.Unknown): Promise<AxiosResponse<DealChecklistItem[]>> =>
		request.get(`/Checklist/FIMenu/GetAllRequired?dealNumber=${dealNumber}&section=${section}`),

	fimenu_getItem: (dealId: string, itemId: string, setIndex?: number): Promise<AxiosResponse<DealChecklistItem>> => {
		let requestURL = `/Checklist/FIMenu/GetItem?dealId=${dealId}&itemId=${itemId}`;
		if (setIndex != null) requestURL += `&setIndex=${setIndex}`;

		return request.get(requestURL);
	},

	fimenu_addCustomItem: (customChecklistRequest: IChecklistAddCustomRequest): Promise<AxiosResponse<IElasticResponseClass>> =>
		request.post(`/Checklist/FIMenu/AddCustomItem`, customChecklistRequest),

	fimenu_editCustomItem: (customChecklistRequest: IChecklistAddCustomRequest): Promise<AxiosResponse<IElasticResponseClass>> =>
		request.post(`/Checklist/FIMenu/EditCustomItem`, customChecklistRequest),

	fimenu_deleteCustomItem: (dealId: string, checklistItemId: string): Promise<AxiosResponse<IElasticResponseClass>> =>
		request.post(`/Checklist/FIMenu/DeleteCustomItem`, {
			dealId,
			checklistItemId,
		}),

	fimenu_sendLink: (
		appDomain: string,
		storeCode: string,
		dealId: string,
		forWhom: PersonType,
		phoneNumber: string = null,
		emailAddress: string = null,
	): Promise<AxiosResponse<ISimpleResponse>> =>
		request.post(`/Checklist/FIMenu/SendLink`, {
			appDomain,
			storeCode,
			dealId,
			forWhom,
			phoneNumber,
			emailAddress,
		}),

	fimenu_getLink: (appDomain: string, dealId: string, personType: PersonType): Promise<AxiosResponse<string>> =>
		request.get(`/Checklist/FIMenu/GetLink?appDomain=${appDomain}&dealId=${dealId}&personType=${personType}`),
	// #endregion

	// #region Customer Routes
	customer_getAllRequired: (customerId: string, dealId: string): Promise<AxiosResponse<DealChecklistItem[]>> =>
		request.get(`/Checklist/Customer/GetAllRequired?dealId=${dealId}&customerId=${customerId}`),

	customer_getUpdatedItem: (dealId: string, itemId: string, forWhom: PersonType, setIndex?: number): Promise<AxiosResponse<DealChecklistItem>> => {
		let requestURL = `/Checklist/Customer/GetUpdatedItem?dealId=${dealId}&itemId=${itemId}&forWhom=${forWhom}`;
		if (setIndex != null) requestURL += `&setIndex=${setIndex}`;

		return request.get(requestURL);
	},
	// #endregion

	// #region Deprecated Routes
	// Deprecated Endpoints. Leaving these here for now so the old checklist portal still works.
	getChecklistForCustomer: (checklistPortalId: string): Promise<AxiosResponse<any>> =>
		request.get(`/Checklist/GetChecklistForCustomer?checklistPortalId=${checklistPortalId}`),

	resetItem: (checklistVerification: IChecklistVerification): Promise<AxiosResponse<boolean>> => request.post(`/Checklist/ResetChecklistItem`, checklistVerification),
	// #endregion
};
